import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import CampaignIcon from '@mui/icons-material/Campaign';
import AdjustIcon from '@mui/icons-material/Adjust';
import TuneIcon from '@mui/icons-material/Tune';
import TvIcon from '@mui/icons-material/Tv';
import GroupsIcon from '@mui/icons-material/Groups';
import BadgeIcon from '@mui/icons-material/Badge';
import BarChartIcon from '@mui/icons-material/BarChart';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import LanguageIcon from '@mui/icons-material/Language';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useMatch } from '../contexts/MatchContext';
import LANGUAGES from '../i18n/languages';

export const DRAWER_WIDTH = 260;

const MENU_ITEMS = [
  { path: '', label: 'manage.dashboard', icon: <DashboardIcon />, permission: 'manage' },
  { path: 'settings', label: 'manage.settings', icon: <SettingsIcon />, permission: 'manage' },
  { divider: true },
  { path: 'shooters', label: 'manage.shooters', icon: <PeopleIcon />, permission: 'registration' },
  { path: 'stages', label: 'manage.stages', icon: <DirectionsRunIcon />, permission: 'stages' },
  { path: 'squads', label: 'manage.squads', icon: <GroupWorkIcon />, permission: 'squads' },
  { path: 'announcements', label: 'manage.announcements', icon: <CampaignIcon />, permission: 'manage' },
  { divider: true },
  { path: 'scores', label: 'manage.enterScores', icon: <AdjustIcon />, permission: 'score' },
  { path: 'chrono', label: 'manage.chrono', icon: <TuneIcon />, permission: 'chrono' },
  { path: 'score-table', label: 'manage.liveScores', icon: <TvIcon />, permission: 'score' },
  { divider: true },
  { path: 'teams', label: 'manage.teams', icon: <GroupsIcon />, permission: 'manage' },
  { path: 'staff', label: 'manage.staff', icon: <BadgeIcon />, permission: 'staff' },
  { path: 'stats', label: 'manage.stats', icon: <BarChartIcon />, permission: 'stats' },
  { path: 'winmss', label: 'manage.winmss', icon: <SwapVertIcon />, permission: 'manage' },
  { divider: true },
  { path: 'agent', label: 'agent.title', icon: <SmartToyIcon />, agentOnly: true },
];

const drawerPaperSx = {
  width: DRAWER_WIDTH,
  boxSizing: 'border-box',
  backgroundImage: 'url(https://endofscoring.s3.eu-central-1.amazonaws.com/login-bg.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: '#fff',
  border: 'none',
};

export default function ManageSidebar({ permissions, mobileOpen, onClose }) {
  const { t, i18n } = useTranslation();
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);
  const [serverVersion, setServerVersion] = useState('...');
  useEffect(() => {
    const base = import.meta.env.VITE_API_URL || '';
    axios.get(`${base}/api/version`).then(({ data }) => setServerVersion(data.version || '?')).catch(() => setServerVersion('?'));
  }, []);
  const navigate = useNavigate();
  const { matchId } = useParams();
  const location = useLocation();
  const matchContext = useMatch();
  const match = matchContext?.match;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const basePath = `/${matchId}/manage`;
  const currentSubPath = location.pathname.replace(basePath, '').replace(/^\//, '');

  const { user: authUser } = useAuth();

  const isAdmin = permissions?.admin || permissions?.owner ||
    permissions?.role === 'owner' || permissions?.permissions?.includes('manage');

  const AGENT_ALLOWED_EMAILS = ['yoav@endofscoring.com', 'amit.yoav@gmail.com'];

  const hasPermission = (item) => {
    if (item.agentOnly) return AGENT_ALLOWED_EMAILS.includes(authUser?.email);
    if (!item.permission) return true;
    if (isAdmin) return true;
    if (permissions?.permissions?.includes(item.permission)) return true;
    return permissions?.[item.permission];
  };

  const handleDisconnect = () => {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('auth');
    sessionStorage.removeItem('cognitoIdToken');
    window.location.reload();
  };

  const handleNavigate = (path) => {
    if (isMobile) onClose?.();
    navigate(`${basePath}/${path}`);
  };

  const matchTitle = match?.title || matchId;
  const matchUrl = `${window.location.origin}/${match?.alias || matchId}`;
  const staffName = permissions?.name || '';

  const drawerContent = (
    <>
      {/* Dark overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 0,
        }}
      />

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {matchTitle}
          </Typography>
          <Typography
            variant="caption"
            component="a"
            href={matchUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'underline', display: 'block', mt: 0.5, wordBreak: 'break-all' }}
          >
            {matchUrl}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            {staffName && (
              <Typography variant="body2">
                {t('manage.hey', 'Hey')} {staffName}!
              </Typography>
            )}
            <Typography
              variant="body2"
              component="span"
              onClick={handleDisconnect}
              sx={{ textDecoration: 'underline', cursor: 'pointer', color: 'rgba(255,255,255,0.8)' }}
            >
              {t('manage.disconnect', 'Disconnect')}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

        {/* Menu */}
        <List sx={{ flex: 1, overflowY: 'auto', pt: 0, pb: 8 }}>
          {MENU_ITEMS.map((item, index) => {
            if (item.divider) {
              return <Divider key={`divider-${index}`} sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />;
            }

            if (!hasPermission(item)) {
              return null;
            }

            const isActive = item.path === ''
              ? currentSubPath === ''
              : currentSubPath.startsWith(item.path);

            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  selected={isActive}
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    py: 1.5,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(100,181,246,0.7)',
                      '&:hover': { backgroundColor: 'rgba(100,181,246,0.8)' },
                    },
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 48, color: '#fff' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(item.label)}
                    primaryTypographyProps={{ sx: { color: '#fff', fontSize: '0.95rem' } }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
        <Box sx={{ position: 'fixed', bottom: 0, left: 0, width: DRAWER_WIDTH, boxSizing: 'border-box', zIndex: 2, backgroundColor: 'rgba(0,0,0,0.85)' }}>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'rgba(255,255,255,0.4)', pt: 1, fontSize: '0.65rem' }}>
            Client: {__APP_VERSION__} | Server: {serverVersion}
          </Typography>
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box
            onClick={(e) => setLangMenuAnchor(e.currentTarget)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', color: 'rgba(255,255,255,0.8)' }}
          >
            <LanguageIcon fontSize="small" />
            <Typography variant="body2">
              {(LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0]).label}
            </Typography>
          </Box>
          <Menu
            anchorEl={langMenuAnchor}
            open={Boolean(langMenuAnchor)}
            onClose={() => setLangMenuAnchor(null)}
          >
            {LANGUAGES.map((lang) => (
              <MenuItem
                key={lang.code}
                selected={i18n.language === lang.code}
                onClick={() => { i18n.changeLanguage(lang.code); localStorage.setItem('userLanguage', lang.code); setLangMenuAnchor(null); }}
              >
                {lang.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
        </Box>
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        className="noprint"
        sx={{ '& .MuiDrawer-paper': drawerPaperSx }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      className="noprint"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': { ...drawerPaperSx, position: 'relative' },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
